/**
 * The text comms feature (M7 plan S31; spec §2.7, §3.1; the career only). Order `FEATURE_ORDER.comms` 545.
 *
 * - **Hails:** the story link posts its results' effects (`postStoryEffects`, `port.ts`); a `comms` node or a
 *   comms-channel callback waits as a hail. The HUD prompt (the status stack's hail chip, `hud.ts`) shows the oldest,
 *   "<name> is hailing · [O] Answer" (bindings.json keyboard `answerHail`), and never times out.
 * - **Panel:** since card M3 (dashboard spec §1.2, §2.2) the panel is the dashboard's **Log › Comms** section
 *   (`registerDashSection`; no modal, frame or pause-menu tab of its own). O in flight answers the oldest hail and
 *   opens the dashboard there; O while that section is showing answers too. The section shows the speaker, a
 *   code-drawn placeholder portrait (M6's), the lines and 2–4 replies, "Show [log]" replies marked; up/down and
 *   Enter (the dashboard's focus inputs) or a click pick. With no conversation open it offers the waiting hail's
 *   Answer button (the pad's way in). Picks go to the story port (`bindCommsStory`). The comms babble
 *   (`src/audio/comms`) speaks under each line.
 * - **Closing:** closing the dashboard or leaving the section closes the conversation without a reply (it waits
 *   again at its node), as Esc did on the old panel.
 * - The hotkey answers only in flight (not paused, docked or under another modal).
 *
 * `frame` allocates nothing unless a hail arrives, the prompt changes or the session changes (D-014, the M1 HUD rule).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import { anyKeyHeld } from '../../reach/input'
import { dashboardOpen, openDashboard, registerDashSection, type DashContext, type DashSection, type DashTarget } from '../../ui/dashboard/registry'
import audioFeature from '../audio/feature'
import { createBabble, type Babble, type BabbleVoice } from './babble'
import { mountHailPrompt, writeHailPrompt, type HailPromptView } from './hud'
import {
  answerHail,
  chooseReply,
  closePanel,
  createCommsSession,
  enqueueHail,
  factionTints,
  hailOf,
  hailPromptText,
  loadCommsUiConfig,
  panelModel,
  storySpeakers,
  waitingHail,
  type CommsSession,
  type CommsUiConfig,
  type PanelDeps,
  type PanelModel,
  type ReplyOutcome,
} from './model'
import { commsStory, takeHailEffects, type HailEffect } from './port'
import { createCommsSection } from './section'
import './comms.css'

/** The Log › Comms section id (dashboard spec §2.2). */
export const COMMS_SECTION_ID = 'log.comms'
/** Where O opens the dashboard. */
export const COMMS_TARGET: DashTarget = Object.freeze({ tab: 'log', section: COMMS_SECTION_ID })

export interface CommsFeature extends GameFeature {
  readonly session: CommsSession
  readonly view: HailPromptView | null
  /** True while a conversation is open on the panel. */
  readonly panelOpen: boolean
  /** What the open panel shows (null when no conversation is open). */
  readonly model: PanelModel | null
  /** Answers the oldest waiting hail and opens the dashboard on Log › Comms; false when nothing waits or one is open. */
  answer(): boolean
  /** Opens the dashboard on Log › Comms (no answer: the section offers the waiting hail). */
  openPanel(): void
  /** Picks a reply on the open conversation (focus + Enter, a click). */
  reply(id: string): ReplyOutcome
  /** Closes the open conversation without a reply; a conversation waits again at its node. */
  closePanel(): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/** The dashboard calls the feature makes (default: the registry's; Node tests pass stand-ins). */
export interface CommsDashPort {
  register(s: DashSection): () => void
  open(at: DashTarget): void
  isOpen(): boolean
}

const REGISTRY_PORT: CommsDashPort = { register: registerDashSection, open: openDashboard, isOpen: dashboardOpen }

export interface CommsFeatureOptions {
  /** The audio feature's comms (default: the audio feature). */
  audio?: { readonly comms: BabbleVoice | null }
  dash?: CommsDashPort
}

export function createCommsFeature(opts: CommsFeatureOptions = {}): CommsFeature {
  const audio = opts.audio ?? audioFeature
  const dash = opts.dash ?? REGISTRY_PORT
  const session = createCommsSession()
  let ctxRef: GameContext | null = null
  let cfg: CommsUiConfig | null = null
  let deps: PanelDeps | null = null
  /** The prompt's hotkeys (bindings.json keyboard `answerHail`), read at init. */
  let keys: readonly string[] = []
  let view: (HailPromptView & { readonly el: HTMLElement }) | null = null
  let babble: Babble | null = null
  let model: PanelModel | null = null
  let renderedVersion = -1
  let babbleKey: string | null = null
  /** Mounted Log › Comms section bodies (0 or 1). */
  let viewing = 0
  let keyWasDown = true
  const listeners = new Set<() => void>()
  const offs: (() => void)[] = []

  const onEffect = (e: HailEffect): void => {
    enqueueHail(session, hailOf(e))
  }

  /** The session changed: rebuild the panel model, babble a new node, tell the section. */
  function update(): void {
    const d = deps
    if (d === null) return
    renderedVersion = session.version
    model = session.open === null ? null : panelModel(session, { ...d, story: commsStory() })
    const m = model
    if (m === null) {
      if (babbleKey !== null) babble?.stop()
      babbleKey = null
    } else if (m.key !== babbleKey) {
      babbleKey = m.key
      babble?.play(m.voice, m.key, m.lines.length)
    }
    for (const fn of [...listeners]) fn()
  }

  const feature: CommsFeature = {
    name: 'comms',
    order: FEATURE_ORDER.comms,
    session,
    get view(): HailPromptView | null {
      return view
    },
    get panelOpen(): boolean {
      return session.open !== null
    },
    get model(): PanelModel | null {
      return model
    },

    init(ctx: GameContext): void {
      if (ctx.mode !== 'career') return
      const c = loadCommsUiConfig()
      ctxRef = ctx
      cfg = c
      const d: PanelDeps = { story: null, speakers: storySpeakers(), tints: factionTints(c), labels: c.labels }
      deps = d
      keys = ctx.data.bindings.keyboard.answerHail
      view = mountHailPrompt(ctx.hudRoot)
      babble = createBabble({ comms: () => audio.comms, stress: c.stress, gapS: c.lineGapS })
      const component = createCommsSection({
        labels: c.labels,
        model: () => model,
        answerText: () => {
          const h = waitingHail(session)
          return h === null ? null : hailPromptText(h, d.speakers, c.labels, keys)
        },
        answer: () => {
          if (answerHail(session)) update()
        },
        reply: (id) => void feature.reply(id),
        leave: () => feature.closePanel(),
        subscribe: (fn) => {
          listeners.add(fn)
          viewing++
          return () => {
            listeners.delete(fn)
            viewing--
          }
        },
      })
      offs.push(
        dash.register({
          id: COMMS_SECTION_ID,
          tab: 'log',
          order: 1,
          label: c.labels.tab,
          available: (dc: DashContext) => dc.mode === 'career',
          badge: () => session.queue.length,
          component,
        }),
      )
    },

    answer(): boolean {
      if (ctxRef === null || !answerHail(session)) return false
      dash.open(COMMS_TARGET)
      update()
      return true
    },

    openPanel(): void {
      if (ctxRef !== null) dash.open(COMMS_TARGET)
    },

    reply(id: string): ReplyOutcome {
      const out = chooseReply(session, commsStory(), id)
      if (out !== 'refused') update()
      return out
    },

    closePanel(): void {
      if (session.open === null) return
      closePanel(session)
      update()
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const v = view
      const c = cfg
      const d = deps
      if (v === null || c === null || d === null) return
      takeHailEffects(onEffect)
      // The dashboard closed (or never opened) with a conversation on: it waits again at its node.
      if (session.open !== null && !dash.isOpen()) closePanel(session)
      writeHailPrompt(v, session, d.speakers, c.labels, keys)
      if (renderedVersion !== session.version) update()

      // The hotkey: an edge. In flight (not paused, docked or under a modal) it answers and opens Log › Comms;
      // with that section showing it answers there.
      const down = anyKeyHeld(ctx.input.keys, keys)
      const pressed = down && !keyWasDown
      keyWasDown = down
      if (!pressed || f.paused) return
      if (f.held !== true && ctx.ui.modal === 0) feature.answer()
      else if (viewing > 0 && dash.isOpen() && answerHail(session)) update()
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      babble?.stop()
      babbleKey = null
      listeners.clear()
      viewing = 0
      view?.el.remove()
      view = null
      cfg = null
      deps = null
      babble = null
      model = null
      ctxRef = null
      session.queue.length = 0
      session.open = null
      session.nodeId = null
    },
  }
  return feature
}

const feature: CommsFeature = createCommsFeature()

export default feature
